import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, QrCode, Bell, Users, Smartphone, CheckCircle2, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ONBOARDING_KEY = 'lembrou_onboarding_completed';
const ONBOARDING_CORRETOR_KEY = 'lembrou_onboarding_corretor_completed';

// Componente Plus para ilustração (deve vir ANTES de steps)
const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

// ── Dados dos passos do onboarding PADRÃO (PJ geral) ───────────────────────
const stepsPadrao = [
  {
    id: 'welcome',
    title: 'Bem-vindo ao Lembrou! 🎉',
    description: 'O sistema que ajuda você a nunca perder um cliente por esquecimento. Vamos fazer um tour rápido?',
    icon: '🗓️',
    illustration: (
      <div className="relative w-48 h-48 mx-auto">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full animate-pulse" />
        <div className="absolute inset-4 bg-white rounded-full shadow-lg flex items-center justify-center">
          <span className="text-7xl">🗓️</span>
        </div>
        <div className="absolute -top-2 -right-2 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
          <Bell className="w-6 h-6 text-white" />
        </div>
      </div>
    ),
    highlight: null,
  },
  {
    id: 'qrcode',
    title: 'Seu QR Code Exclusivo',
    description: 'Cada estabelecimento tem um QR Code único. Imprima e coloque em local visível para seus clientes escanearem e se cadastrarem automaticamente!',
    icon: '📱',
    illustration: (
      <div className="relative w-48 h-48 mx-auto">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl" />
        <div className="absolute inset-2 bg-white rounded-2xl shadow-xl p-4 flex flex-col items-center justify-center">
          <QrCode className="w-20 h-20 text-gray-800 mb-2" />
          <div className="text-xs text-gray-500 text-center">Clique em "Exportar QR Code" no topo da tela</div>
        </div>
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
          ↑ Botão no Header
        </div>
      </div>
    ),
    highlight: 'qrcode-button',
  },
  {
    id: 'cadastro-cliente',
    title: 'Cadastro Automático via QR Code',
    description: 'Quando o cliente escaneia o QR Code, ele é direcionado para uma página onde preenche seus dados. Automaticamente você recebe o cadastro no seu painel!',
    icon: '👤',
    illustration: (
      <div className="relative w-64 h-48 mx-auto">
        {/* Celular */}
        <div className="absolute left-0 top-0 w-24 h-44 bg-gray-800 rounded-2xl p-1 shadow-xl">
          <div className="w-full h-full bg-white rounded-xl flex flex-col items-center justify-center p-2">
            <QrCode className="w-8 h-8 text-gray-400 mb-1" />
            <div className="text-[8px] text-gray-500 text-center">Cliente escaneia</div>
          </div>
        </div>
        {/* Seta animada */}
        <div className="absolute left-28 top-1/2 -translate-y-1/2 flex items-center">
          <div className="flex space-x-1 animate-pulse">
            <div className="w-2 h-2 bg-primary rounded-full" />
            <div className="w-2 h-2 bg-primary/70 rounded-full" />
            <div className="w-2 h-2 bg-primary/40 rounded-full" />
          </div>
        </div>
        {/* Dashboard */}
        <div className="absolute right-0 top-0 w-28 h-44 bg-white rounded-xl shadow-xl border-2 border-primary/20 p-2">
          <div className="w-full h-3 bg-gray-100 rounded mb-2" />
          <div className="flex items-center gap-1 bg-green-50 rounded p-1 mb-1">
            <Users className="w-3 h-3 text-green-600" />
            <span className="text-[7px] text-green-700 font-medium">Novo cliente!</span>
          </div>
          <div className="space-y-1">
            <div className="w-full h-2 bg-gray-100 rounded" />
            <div className="w-3/4 h-2 bg-gray-100 rounded" />
            <div className="w-1/2 h-2 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    ),
    highlight: null,
  },
  {
    id: 'lembretes',
    title: 'Cadastre Serviços e Datas',
    description: 'Adicione os serviços de cada cliente com a data de vencimento. O Lembrou vai cuidar de lembrar eles automaticamente!',
    icon: '📝',
    illustration: (
      <div className="relative w-56 h-48 mx-auto">
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <span className="text-lg">👤</span>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800">João Silva</div>
              <div className="text-xs text-gray-400">Cliente</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2">
              <span className="text-xs text-gray-600">Serviço</span>
              <span className="text-xs font-medium text-gray-800">Seguro Auto</span>
            </div>
            <div className="flex justify-between items-center bg-orange-50 rounded-lg p-2">
              <span className="text-xs text-gray-600">Vence em</span>
              <span className="text-xs font-medium text-orange-600">15 dias</span>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
          <PlusIcon className="w-5 h-5 text-white" />
        </div>
      </div>
    ),
    highlight: 'novo-lembrete-button',
  },
  {
    id: 'notificacoes',
    title: 'Notificações Automáticas',
    description: 'Seus clientes recebem lembretes automáticos por Email e WhatsApp nos dias D-15, D-7 e no dia do vencimento. Você não precisa fazer nada!',
    icon: '🔔',
    illustration: (
      <div className="relative w-56 h-48 mx-auto flex items-center justify-center">
        <div className="relative">
          {/* Centro - Lembrou */}
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg z-10 relative">
            <Bell className="w-8 h-8 text-white" />
          </div>
          
          {/* D-15 */}
          <div className="absolute -top-12 -left-8 bg-yellow-100 rounded-xl px-3 py-2 shadow-md animate-pulse">
            <div className="text-xs font-bold text-yellow-700">D-15</div>
            <div className="text-[10px] text-yellow-600">15 dias antes</div>
          </div>
          
          {/* D-7 */}
          <div className="absolute -top-8 -right-12 bg-orange-100 rounded-xl px-3 py-2 shadow-md animate-pulse" style={{ animationDelay: '0.2s' }}>
            <div className="text-xs font-bold text-orange-700">D-7</div>
            <div className="text-[10px] text-orange-600">7 dias antes</div>
          </div>
          
          {/* D-0 */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-red-100 rounded-xl px-3 py-2 shadow-md animate-pulse" style={{ animationDelay: '0.4s' }}>
            <div className="text-xs font-bold text-red-700">D-0</div>
            <div className="text-[10px] text-red-600">No dia!</div>
          </div>
          
          {/* Ícones de canal */}
          <div className="absolute -left-14 top-1/2 -translate-y-1/2 flex flex-col gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm">📧</span>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-green-600" />
            </div>
          </div>
        </div>
      </div>
    ),
    highlight: null,
  },
  {
    id: 'pronto',
    title: 'Pronto para Começar! 🚀',
    description: 'Agora você sabe como funciona o Lembrou. Comece cadastrando seus clientes ou imprima seu QR Code para eles se cadastrarem sozinhos!',
    icon: '✅',
    illustration: (
      <div className="relative w-48 h-48 mx-auto">
        <div className="absolute inset-0 bg-gradient-to-br from-green-100 to-green-50 rounded-full" />
        <div className="absolute inset-4 bg-white rounded-full shadow-lg flex items-center justify-center">
          <CheckCircle2 className="w-20 h-20 text-green-500" />
        </div>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-primary text-white text-sm px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap font-medium">
          Vamos lá!
        </div>
      </div>
    ),
    highlight: null,
    showFaqLink: true,
  },
];

// ── Dados dos passos do onboarding para CORRETORES DE SEGUROS ───────────────
// TODO: Implementar onboarding específico para corretores
const stepsCorretor = [
  // Será implementado futuramente com fluxo específico para corretores
  // Por enquanto, retorna array vazio para não mostrar onboarding
];

// ── Componente Principal ─────────────────────────────────────────────────────
const Onboarding = ({ onComplete, forceShow = false, tipoUsuario = 'padrao' }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Seleciona os steps baseado no tipo de usuário
  const steps = tipoUsuario === 'corretor' ? stepsCorretor : stepsPadrao;
  const storageKey = tipoUsuario === 'corretor' ? ONBOARDING_CORRETOR_KEY : ONBOARDING_KEY;

  useEffect(() => {
    // Se for corretor e não tiver steps definidos, não mostra nada
    if (tipoUsuario === 'corretor' && stepsCorretor.length === 0) {
      setIsVisible(false);
      return;
    }

    // Verificar se já completou o onboarding
    const completed = localStorage.getItem(storageKey);
    if (!completed || forceShow) {
      setIsVisible(true);
    }
  }, [forceShow, tipoUsuario, storageKey]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const goToFaq = () => {
    handleComplete();
    navigate('/faq');
  };

  // Não renderiza se não tiver steps ou não estiver visível
  if (!isVisible || steps.length === 0) return null;

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[100]">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-md overflow-hidden max-h-[95vh] overflow-y-auto">
        {/* Header com barra de progresso */}
        <div className="relative">
          {/* Barra de progresso */}
          <div className="h-1.5 bg-gray-100">
            <div 
              className="h-full bg-gradient-to-r from-primary to-primary transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Botão fechar */}
          <button
            onClick={handleSkip}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>

          {/* Indicador de passos */}
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4 text-xs text-gray-400 font-medium">
            {currentStep + 1} de {steps.length}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-4 sm:p-8 pt-4 sm:pt-6">
          {/* Ilustração */}
          <div className="mb-4 sm:mb-6 min-h-[160px] sm:min-h-[200px] flex items-center justify-center transform scale-90 sm:scale-100">
            {step.illustration}
          </div>

          {/* Título e descrição */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2 sm:mb-3">
            {step.title}
          </h2>
          <p className="text-sm sm:text-base text-gray-500 text-center leading-relaxed mb-4 sm:mb-6">
            {step.description}
          </p>

          {/* Link para FAQ no último passo */}
          {step.showFaqLink && (
            <button
              onClick={goToFaq}
              className="flex items-center justify-center gap-2 text-primary hover:opacity-80 text-sm font-medium mx-auto mb-4 sm:mb-6 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              Ver todas as perguntas frequentes
            </button>
          )}

          {/* Checkbox "Não mostrar novamente" no último passo */}
          {currentStep === steps.length - 1 && (
            <label className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4 sm:mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Não mostrar novamente
            </label>
          )}

          {/* Navegação */}
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="rounded-xl px-2 sm:px-4 text-sm"
            >
              <ChevronLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Voltar</span>
            </Button>

            {/* Dots indicadores */}
            <div className="flex gap-1 sm:gap-1.5">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentStep 
                      ? 'bg-primary w-5 sm:w-6' 
                      : index < currentStep 
                        ? 'bg-primary/50 w-2' 
                        : 'bg-gray-200 w-2'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={handleNext}
              className="rounded-xl px-3 sm:px-4 bg-primary hover:bg-primary/90 text-white text-sm"
            >
              {currentStep === steps.length - 1 ? 'Começar' : 'Próximo'}
              {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 sm:ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Função para resetar o onboarding (útil para testes) ─────────────────────
export const resetOnboarding = (tipo = 'padrao') => {
  if (tipo === 'corretor') {
    localStorage.removeItem(ONBOARDING_CORRETOR_KEY);
  } else {
    localStorage.removeItem(ONBOARDING_KEY);
  }
};

// ── Função para verificar se onboarding foi completado ──────────────────────
export const isOnboardingCompleted = (tipo = 'padrao') => {
  const key = tipo === 'corretor' ? ONBOARDING_CORRETOR_KEY : ONBOARDING_KEY;
  return localStorage.getItem(key) === 'true';
};

export default Onboarding;
